import { Variant } from "@/types/types.ts";

type VariantService = NonNullable<Variant["services"]>[number];
type IncludedGuaranteeService = Extract<
  VariantService,
  { __typename: "IncludedGuarantee" }
>;

export type ExtendedWarrantyService = Extract<
  VariantService,
  { __typename: "ExtendedWarranty" }
>;

export type MatchMediaState = {
  smallMobile: boolean;
  largeMobile: boolean;
  verySmallDt: boolean;
  smallDesktop: boolean;
  largeDesktop: boolean;
  largeDesktopAbove: boolean;
};

export const getCardsPerPage = (
  cardsLength: number,
  matchMediaState: MatchMediaState,
) => {
  if (matchMediaState.largeDesktopAbove) {
    return cardsLength || 1;
  }

  if (matchMediaState.largeDesktop) {
    return cardsLength <= 4 ? cardsLength || 1 : 4;
  }

  if (matchMediaState.smallDesktop) {
    return cardsLength <= 3 ? cardsLength || 1 : 3;
  }

  if (matchMediaState.verySmallDt) {
    return cardsLength <= 2 ? cardsLength || 1 : 2;
  }

  return 1;
};

export const shouldShowChevronNavigation = (matchMediaState: MatchMediaState) =>
  (matchMediaState.smallDesktop || matchMediaState.largeDesktop) &&
  !matchMediaState.largeDesktopAbove;

export const getClosestCardIndex = (
  items: HTMLElement[],
  scrollLeft: number,
) => {
  if (!items.length) {
    return 0;
  }

  const offsets = items
    .map((item) => item.offsetLeft)
    .filter((offset): offset is number => typeof offset === "number");

  if (offsets.length > 1 && new Set(offsets).size > 1) {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  const parent = items[0]?.parentElement;
  const scrollWidth = parent?.scrollWidth ?? 0;
  const clientWidth = parent?.clientWidth ?? 0;

  if (scrollWidth > clientWidth) {
    const cardWidth = scrollWidth / items.length;
    return Math.round(scrollLeft / cardWidth);
  }

  return 0;
};

// todo - move to global utils for C&C
export const formatOrderByTime = (time: string): string => {
  const [h, m] = time.split(":").map((t) => parseInt(t, 10));
  const hours = h || 0;
  const minutes = m || 0;
  const hour12 = hours % 12 || 12;
  const suffix = hours >= 12 ? "pm" : "am";
  return minutes === 0
    ? `${hour12}${suffix}`
    : `${hour12}:${minutes.toString().padStart(2, "0")}${suffix}`;
};

export function isIncludedGuarantee(
  service: VariantService,
): service is IncludedGuaranteeService {
  return service.__typename === "IncludedGuarantee";
}

export function isProtectPlusExtendedWarranty(
  service: VariantService,
): service is ExtendedWarrantyService {
  return (
    service.__typename === "ExtendedWarranty" &&
    service.title.includes("Protect Plus")
  );
}
